import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

/**
 * PersonalizationContract — the route-owned aggregation point for personalized, user-specific, and
 * feature-affordance View Page initial-load data.
 *
 * See confluence/openspec/changes/personalization-contract.
 *
 * Every top-level field is gated by an `@include` variable that defaults to `false`, so this
 * operation is never a promise that every viewer pays for every feature. Each include variable is
 * built by preloadViewPagePersonalization from the corresponding legacy eligibility and piece
 * controls. The route combines Loom, presence, and onboarding; recommendations and catchup retain
 * independent completion boundaries.
 *
 * Field groups are kept contiguous and independently gated so that splitting this operation later
 * is a matter of moving spreads into a second document rather than untangling interleaved
 * selections.
 *
 * Fragment spreads here are masked, unlike the unmasked spreads in the legacy package-owned
 * operations. Masked spreads produce the fragment refs that the gated readers will consume with
 * `useFragment`; the legacy operations stay unmasked because their `$data` is still read directly
 * through interop.
 *
 * `$recommendationExperience` is non-nullable because the schema declares
 * `getRecommendedPages(… experience: String!)`, and Relay rejects default values on non-nullable
 * variables — so it cannot be omitted the way the include flags can. Its value comes from an
 * `expVal` call that fires an exposure event, so the caller must pass a placeholder rather than
 * evaluating that experiment when `$includeRecommendations` is false; otherwise the experiment's
 * exposure population changes. `@include(if: false)` strips the field, so the placeholder is never
 * used.
 */
export const ViewPagePersonalizationQuery: GraphQLTaggedNode = graphql`
	query ViewPagePersonalizationQuery(
		$contentId: ID!
		$spaceKey: String!
		$cloudId: ID!
		$catchupContentType: CatchupContentType!
		$recommendationEntityType: String!
		$recommendationExperience: String!
		$recommendationSpaceId: ID!
		$onboardingKeys: [String]
		$includeRecommendations: Boolean = false
		$includeOnboardingState: Boolean = false
		$includeAIFloatingContextMenu: Boolean = false
		$includePresenceSettings: Boolean = false
		$includeLoom: Boolean = false
		$includeMeetingRecorderEligibility: Boolean!
	) @throwOnFieldError {
		... @include(if: $includeLoom) @alias(as: "loom") {
			...loomPersonalization_entryPoints
			...loomPersonalization_tenant
		}

		# End-of-page recommendations. Replaces preloadEndOfPageRecommendation /
		# EndOfPageRecommendationQuery once the gated reader is stable.
		space(key: $spaceKey) @include(if: $includeRecommendations) {
			id
		}
		getRecommendedPages(
			entityType: $recommendationEntityType
			entityId: $contentId
			experience: $recommendationExperience
		) @include(if: $includeRecommendations) {
			...EndOfPageRecommendation_recommendedPagesReader
		}
		getRecommendedPagesSpaceStatus(entityId: $recommendationSpaceId)
			@include(if: $includeRecommendations) {
			...EndOfPageRecommendation_spaceStatusReader
		}

		# View-page onboarding state. Replaces the preloadViewPageOnboardingState route callsites.
		onboardingState(key: $onboardingKeys) @include(if: $includeOnboardingState) {
			...onboardingState_state
		}

		# AI floating context menu catchup. Replaces preloadAIFloatingContextMenu.
		catchupGetLastViewedTime(contentId: $contentId, contentType: $catchupContentType)
			@include(if: $includeAIFloatingContextMenu) {
			...useDynamicSplitButton_catchupLastViewedTimeReader
		}

		# Content-view presence setting. Replaces preloadPresenceSettings.
		confluence_teamPresenceContentSetting(cloudId: $cloudId, spaceKey: $spaceKey)
			@include(if: $includePresenceSettings) {
			...ContentHeaderPresence_contentSettingReader
		}
	}
`;
